import clsx from 'clsx';
import dayjs from 'dayjs';
import React from 'react';
import { factory } from '../../../factory/factory.utils.mjs';
import { Box } from '../../../Other/Box/Box.mjs';
import '../DatesProvider/DatesProvider.mjs';
import { useDatesContext } from '../DatesProvider/use-dates-context.mjs';
import { Day } from '../Day/Day.mjs';
import { shiftTimezone } from '../Day/Day.utils.mjs';
import { WeekdaysRow } from '../WeekdaysRow/WeekdaysRow.mjs';
import classes from './Month.module.css.mjs';
import { getMonthDays, getDateInTabOrder, isSameMonth, isBeforeMaxDate, isAfterMinDate } from './Month.utils.mjs';

const Month = factory((props, ref) => {
  const {
    className,
    locale,
    firstDayOfWeek,
    weekdayFormat,
    month,
    weekendDays,
    getDayProps,
    excludeDate,
    minDate,
    maxDate,
    renderDay,
    hideOutsideDates,
    hideWeekdays,
    getDayAriaLabel,
    isStatic,
    focusOutsideMonthDays,
    __getDayRef,
    __onDayKeyDown,
    __onDayClick,
    __onDayMouseEnter,
    __preventFocus,
    __stopPropagation,
    ...others
  } = props;
  const ctx = useDatesContext();
  const dates = getMonthDays({
    month,
    firstDayOfWeek: ctx.getFirstDayOfWeek(firstDayOfWeek),
    consistentWeeks: ctx.consistentWeeks
  });
  const dateInTabOrder = getDateInTabOrder(dates, minDate, maxDate, getDayProps, excludeDate, hideOutsideDates, month);
  const rows = dates.map((row, rowIndex) => {
    const cells = row.map((date, cellIndex) => {
      const outside = !isSameMonth(date, month);
      const isToday = dayjs(date).isSame(shiftTimezone("add", /* @__PURE__ */ new Date(), ctx.getTimezone()), "day");
      const ariaText = getDayAriaLabel?.(date) || dayjs(date).locale(locale || ctx.locale).format("dddd, D MMMM YYYY");
      const dayProps = getDayProps?.(date);
      const isDateInTabOrder = dayjs(date).isSame(dateInTabOrder, "date");
      return /* @__PURE__ */ React.createElement("td", { key: date.toString(), className: classes.monthCell }, /* @__PURE__ */ React.createElement(
        Day,
        {
          "data-kohler-stop-propagation": __stopPropagation || void 0,
          renderDay,
          date,
          weekend: ctx.getWeekendDays(weekendDays).includes(date.getDay()),
          outside: focusOutsideMonthDays ? false : outside,
          hidden: hideOutsideDates ? outside : false,
          "aria-label": isToday ? `Today, ${ariaText}` : ariaText,
          "aria-current": isToday ? "date" : void 0,
          isStatic,
          disabled: excludeDate?.(date) || !isBeforeMaxDate(date, maxDate) || !isAfterMinDate(date, minDate),
          ref: (node) => __getDayRef?.(rowIndex, cellIndex, node),
          ...dayProps,
          onKeyDown: (event) => {
            dayProps?.onKeyDown?.(event);
            __onDayKeyDown?.(event, { rowIndex, cellIndex, date });
          },
          onMouseEnter: (event) => {
            dayProps?.onMouseEnter?.(event);
            __onDayMouseEnter?.(event, date);
          },
          onClick: (event) => {
            dayProps?.onClick?.(event);
            __onDayClick?.(event, date);
          },
          onMouseDown: (event) => {
            dayProps?.onMouseDown?.(event);
            if (__preventFocus) {
              event.preventDefault();
            }
          },
          tabIndex: __preventFocus || !isDateInTabOrder ? -1 : 0
        }
      ));
    });
    return /* @__PURE__ */ React.createElement("tr", { key: rowIndex, className: classes.tableRow }, cells);
  });
  return /* @__PURE__ */ React.createElement(Box, { component: "table", className: clsx(className, classes.month), ref, role: "grid", ...others }, !hideWeekdays && /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement(WeekdaysRow, { locale, firstDayOfWeek, weekdayFormat })), /* @__PURE__ */ React.createElement("tbody", null, rows));
});

export { Month };
